import { POST_STATUSES, type PostStatus } from '@postpilot/core/domain';
import { Filter, Search, X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { statusLabel } from '@/components/ui/status-config';
import { useAccountsStore } from '@/stores/accounts-store';
import { selectHasActiveFilters, usePostsStore } from '@/stores/posts-store';

export function QueueFilters() {
  const filters = usePostsStore((s) => s.filters);
  const setFilters = usePostsStore((s) => s.setFilters);
  const resetFilters = usePostsStore((s) => s.resetFilters);
  const hasActiveFilters = usePostsStore(selectHasActiveFilters);

  const accounts = useAccountsStore((s) => s.accounts);

  // Local mirror of the search box, so typing feels instant while the *query*
  // is debounced. Binding the input straight to the store would fire a request
  // per keystroke.
  const [search, setSearch] = React.useState(filters.search);

  React.useEffect(() => {
    // 300ms: long enough to coalesce a burst of typing, short enough that the
    // results feel like they're keeping up.
    const timer = setTimeout(() => {
      if (search !== filters.search) setFilters({ search });
    }, 300);

    return () => clearTimeout(timer);
  }, [search, filters.search, setFilters]);

  const toggleStatus = (status: PostStatus) => {
    const next = filters.status.includes(status)
      ? filters.status.filter((s) => s !== status)
      : [...filters.status, status];
    setFilters({ status: next });
  };

  const toggleAccount = (id: string) => {
    const next = filters.accountIds.includes(id)
      ? filters.accountIds.filter((a) => a !== id)
      : [...filters.accountIds, id];
    setFilters({ accountIds: next });
  };

  const activeCount = filters.status.length + filters.accountIds.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-56 flex-1 sm:max-w-xs">
        <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search posts…"
          className="pl-8"
          aria-label="Search posts"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="text-muted-foreground hover:text-foreground absolute right-2 top-1/2 -translate-y-1/2 rounded-sm"
            aria-label="Clear search"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {/* Status */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <Filter />
            Status
            {filters.status.length > 0 && (
              <span className="bg-primary text-primary-foreground ml-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                {filters.status.length}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-44">
          <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {POST_STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={filters.status.includes(status)}
              // Radix closes the menu on select by default. Selecting three
              // statuses would mean re-opening it three times.
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={() => toggleStatus(status)}
            >
              {statusLabel(status)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Account */}
      {accounts.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              Account
              {filters.accountIds.length > 0 && (
                <span className="bg-primary text-primary-foreground ml-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                  {filters.accountIds.length}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="w-52">
            <DropdownMenuLabel>Filter by account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {accounts.map((account) => (
              <DropdownMenuCheckboxItem
                key={account.id}
                checked={filters.accountIds.includes(account.id)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => toggleAccount(account.id)}
              >
                @{account.username}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {(hasActiveFilters || activeCount > 0) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSearch('');
            resetFilters();
          }}
          className="text-muted-foreground"
        >
          <X />
          Clear
        </Button>
      )}
    </div>
  );
}
